import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Button,
  TableCell,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import {
  ModalWrapper,
  CloseButton,
  CloseButtonWrapper,
} from "../style/modalStyle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CommonPagination from "../common-components/CommonPagination";
import { SearchIcon } from "../../assets/Icon/ArrowIcon";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import {
  deleteInvoice,
  getDashboardData,
  getInvoiceList,
  sendInvoice,
  sendStripeCode,
} from "../../stores/clients";
import { useDispatch } from "react-redux";
import { FIlterIcon, TrashIconForPopup } from "../../assets/Icon/headerCicon";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setToast } from "../../stores/toast/toastslice";
import CustomTable from "../common-components/CustomTable";
import CircularProgress from "@mui/material/CircularProgress";
import { formatDate } from "../../utils/functions";
import { getInvoiceViewUrl } from "../../utils/fileUrl";
import { OPTIONS } from "../../utils/constants";
import { CrossIcon, ModalBottom, ModalTop } from "../../assets/Icon/ModalIcon";

const StripeInvoiceTable = ({ type }: { type: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const filterRef = useRef<HTMLDivElement>(null);

  const { data, clientListStatus, total_records, error } = useSelector(
    (state: any) => state.clients
  );

  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [limit] = useState(10);
  const [searchLoading, setSearchLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [openMenuInvoiceId, setOpenMenuInvoiceId] = useState<string | null>(
    null
  );
  const [appliedFilterValues, setAppliedFilterValues] = useState<number[]>([]);
  const [tempFilterValues, setTempFilterValues] = useState<number[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [disconnectAnchorEl, setDisconnectAnchorEl] =
    useState<null | HTMLElement>(null);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [disconnectBtnLoading, setDisconnectBtnLoading] = useState(false);
  const [value, setValue] = React.useState(0);

  const handleOpen = (
    event: React.MouseEvent<HTMLElement>,
    invoiceId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoiceId(invoiceId);
    setOpenMenuInvoiceId(invoiceId);
  };

  const handleClose = (option?: string) => {
    setAnchorEl(null);
    if (option && selectedInvoiceId) {
      handleOptions(option, selectedInvoiceId);
    }
    setSelectedInvoiceId(null);
    setOpenMenuInvoiceId(null);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setSearchLoading(true);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      dispatch<any>(
        getInvoiceList({
          offset: 0,
          search: value,
          filter_by: appliedFilterValues.length
            ? appliedFilterValues
            : undefined,
        })
      ).finally(() => setSearchLoading(false));
    }, 1500);
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  const fetchAgents = () => {
    const offset = (pageNo - 1) * limit;
    setPaginationLoading(true);

    dispatch<any>(
      getInvoiceList({
        offset: offset,
        search: searchValue,
        filter_by: appliedFilterValues.length ? appliedFilterValues : undefined,
      })
    ).finally(() => {
      setPaginationLoading(false);
    });
  };

  useEffect(() => {
    fetchAgents();
  }, [pageNo]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close filter dropdown
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setOpenFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Extract data from the URL after STRIPE success
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (code) {
      setStripeConnecting(true);
      dispatch<any>(sendStripeCode({ code, state })).finally(() =>
        setStripeConnecting(false)
      );
    }
  }, [dispatch]);

  const handleOptions = (option: string, id: string) => {
    const invoice = data?.invoices?.find(
      (inv: any) => String(inv.id) === String(id)
    );
    if (option === "View") {
      if (
        invoice &&
        (invoice.invoice_status === 1 || invoice.invoice_status === 2)
      ) {
        // Open the invoice link in a new tab for Paid or Due. `invoice_pdf` is
        // an S3 key that is often null, so prefer the Stripe hosted urls.
        const invoiceUrl = getInvoiceViewUrl(invoice);
        if (!invoiceUrl) {
          dispatch<any>(
            setToast({
              message: "This invoice has no link available yet.",
              type: "error",
              title: "Error",
              onClose: () => {},
            })
          );
          return;
        }
        window.open(invoiceUrl, "_blank");
      } else {
        navigate("/tools/invoices/customerInfo", {
          state: { invoice_id: id, viewOnly: true },
        });
      }
    } else if (option === "Edit") {
      navigate("/tools/invoices/customerInfo", {
        state: { invoice_id: id, viewOnly: false },
      });
    } else if (option === "Send") {
      setSending(true);
      dispatch<any>(sendInvoice({ invoice_id: Number(id) })).then(
        (result: any) => {
          setSending(false);
          if (result?.payload?.code === 200) {
            dispatch<any>(
              setToast({
                message:
                  result?.payload?.message || "Invoice sent successfully!",
                type: "success",
                title: "Success",
                onClose: () => {},
              })
            );
          } else {
            dispatch<any>(
              setToast({
                message: result?.payload?.message || "Failed to send invoice!",
                type: "error",
                title: "Error",
                onClose: () => {},
              })
            );
          }
        }
      );
    } else if (option === "Delete") {
      setPendingDeleteId(id);
      setDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    setDeleteModalOpen(false);
    dispatch<any>(deleteInvoice(pendingDeleteId)).then((result: any) => {
      dispatch<any>(
        getInvoiceList({
          offset: 0,
          search: searchValue,
          filter_by: appliedFilterValues.length
            ? appliedFilterValues
            : undefined,
        })
      ).then(() => {
        setDeleting(false);
        if (result?.payload.code === 200) {
          dispatch<any>(
            setToast({
              message: result?.payload?.message,
              type: "success",
              title: "Success",
              onClose: () => {},
            })
          );
        } else if (result?.payload?.code === 400) {
          dispatch<any>(
            setToast({
              message: result?.payload?.message || "Failed to delete invoice!",
              type: "error",
              title: "Error",
              onClose: () => {},
            })
          );
        }
        dispatch<any>(getDashboardData({ type: type })).then((result: any) => {
          if (result?.payload?.data) setDashboardData(result.payload?.data);
        });
      });
    });
    setPendingDeleteId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setPendingDeleteId(null);
  };

  return (
    <div
      style={{
        height: data?.invoices?.length > 3 ? "auto" : "70vh",
      }}
    >
      <div
        style={{
          display: isMobile ? "grid" : "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
          padding: 20,
          marginTop: 5,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
        }}
      >
        <div
          style={{
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          Stripe Invoicing
        </div>
        <div
          style={{
            display: isMobile ? "grid" : "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#F6F9FD",
              height: "40px",
              display: "flex",
              alignItems: "center",
              paddingLeft: 10,
              borderRadius: "8px",
              width: isMobile || isTablet ? "250px" : "318px",
              marginTop: isMobile ? 10 : 0,
              position: "relative",
            }}
          >
            <SearchIcon />
            <input
              style={{
                border: "none",
                backgroundColor: "transparent",
                padding: 3,
                outline: "none",
                paddingLeft: 10,
                width: "82%",
              }}
              value={searchValue}
              onChange={handleSearchInput}
              placeholder="Search here..."
            />
            {searchValue && (
              <CloseIcon
                onClick={() => {
                  setSearchValue("");
                  dispatch<any>(
                    getInvoiceList({
                      offset: 0,
                      search: "",
                      filter_by: appliedFilterValues.length
                        ? appliedFilterValues
                        : undefined,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: 8,
                  cursor: "pointer",
                  color: "#97999B",
                  fontSize: 20,
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div ref={filterRef} style={{ position: "relative" }}>
              {/* Red dot indicator when filter is applied */}
              {appliedFilterValues.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    width: 10,
                    height: 10,
                    background: "#F44336",
                    borderRadius: "50%",
                    zIndex: 2,
                    border: "2px solid #fff",
                  }}
                />
              )}
              <Button
                style={{
                  border: "1px solid #EAF1FA",
                  borderRadius: "8px",
                  height: "40px",
                  padding: 15,
                  color: "#6C6F71",
                  fontSize: "12px",
                  fontWeight: 600,
                  gap: 10,
                }}
                onClick={() => {
                  setTempFilterValues(appliedFilterValues);
                  setOpenFilter(!openFilter);
                }}
              >
                <FIlterIcon />
                Filter
              </Button>
              {openFilter && (
                <div
                  style={{
                    position: "absolute",
                    marginTop: 10,
                    backgroundColor: "white",
                    borderRadius: "8px",
                    width: isMobile ? "280px" : "400px",
                    right: isMobile ? "" : "0px",
                    zIndex: 1000,
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    marginBottom: 20,
                  }}
                >
                  <div>
                    <p
                      style={{
                        padding: 20,
                        borderBottom: "1px solid #EDF2F6",
                        color: "#01060B",
                        fontSize: "16px",
                        fontWeight: 500,
                      }}
                    >
                      Filter By Status
                    </p>
                    <div style={{ padding: "10px 20px" }}>
                      {/* Multi-select checkboxes for status */}
                      {[
                        { id: 0, name: "Draft" },
                        { id: 1, name: "Paid" },
                        { id: 2, name: "Due" },
                      ].map((option) => (
                        <div
                          key={option.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "16px",
                              color: "#6C6F71",
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                          >
                            {option.name}
                          </label>
                          <Checkbox
                            checked={tempFilterValues.includes(option.id)}
                            onChange={(e) => {
                              let newValues;
                              if (e.target.checked) {
                                newValues = [...tempFilterValues, option.id];
                              } else {
                                newValues = tempFilterValues.filter(
                                  (id) => id !== option.id
                                );
                              }
                              setTempFilterValues(newValues);
                            }}
                            sx={{
                              color: "#2977CC",
                              "&.Mui-checked": {
                                color: "#2977CC",
                              },
                            }}
                          />
                        </div>
                      ))}
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 20,
                        }}
                      >
                        <Button
                          disabled={tempFilterValues.length === 0}
                          type="submit"
                          sx={{
                            width: "130px",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#fff",
                            height: "36px",
                            background:
                              tempFilterValues.length === 0
                                ? "#BDBDBD"
                                : "linear-gradient(to right, #2977CC, #1D538F)",
                            cursor:
                              tempFilterValues.length === 0
                                ? "not-allowed"
                                : "pointer",
                            opacity: tempFilterValues.length === 0 ? 0.7 : 1,
                          }}
                          onClick={() => {
                            setFilterLoading(true);
                            setAppliedFilterValues(tempFilterValues);
                            dispatch<any>(
                              getInvoiceList({
                                offset: 0,
                                search: searchValue,
                                filter_by: tempFilterValues.length
                                  ? tempFilterValues
                                  : undefined,
                              })
                            ).finally(() => {
                              setFilterLoading(false);
                            });
                            setOpenFilter(false);
                          }}
                        >
                          Apply
                        </Button>
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            background:
                              "linear-gradient(to right, #2977CC, #1D538F)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            cursor:
                              appliedFilterValues.length === 0 &&
                              tempFilterValues.length === 0
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              appliedFilterValues.length === 0 &&
                              tempFilterValues.length === 0
                                ? 0.5
                                : 1,
                          }}
                          onClick={() => {
                            if (
                              appliedFilterValues.length === 0 &&
                              tempFilterValues.length === 0
                            )
                              return;
                            setFilterLoading(true);
                            setAppliedFilterValues([]);
                            setTempFilterValues([]);
                            dispatch<any>(
                              getInvoiceList({
                                offset: 0,
                                search: searchValue,
                                filter_by: undefined,
                              })
                            ).finally(() => {
                              setFilterLoading(false);
                            });
                            setOpenFilter(false);
                          }}
                        >
                          Reset filter
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {data?.account?.account_id && (
              <Button
                type="submit"
                sx={{
                  width: "120px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#fff",
                  height: "40px",
                  background: createLoading ? "#BDBDBD" : "#2977CC",
                  position: "relative",
                  pointerEvents: createLoading ? "none" : "auto",
                  opacity: createLoading ? 0.7 : 1,
                }}
                disabled={createLoading}
                onClick={() => {
                  setCreateLoading(true);
                  setTimeout(() => {
                    navigate("/tools/invoices/customerInfo");
                    setCreateLoading(false);
                  }, 600);
                }}
              >
                {createLoading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Create Invoice"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      {/**TABLE */}
      <div>
        <CustomTable
          headings={[
            "SR NO",
            "client name",
            "Amount",
            "Due Date",
            "Status",
            "Action",
          ]}
        >
          {deleting || filterLoading || searchLoading || paginationLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            data?.invoices?.map((item: any, index: number) => (
              <TableRow key={item.id || index}>
                <TableCell
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {(pageNo - 1) * limit + index + 1}
                </TableCell>
                <TableCell>
                  <Typography
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {item.client_name || item.customer_name || item.name || "-"}
                  </Typography>
                </TableCell>

                <TableCell
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  ${item?.invoice_amount?.toFixed(2)}
                </TableCell>
                <TableCell
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {formatDate(item.invoice_due_date) || "-"}
                </TableCell>
                <TableCell>
                  <Button
                    variant="text"
                    sx={{
                      background:
                        item.invoice_status === 1
                          ? "#D9FFD2"
                          : item.invoice_status === 2 ||
                            item?.invoice_status === 3
                          ? "#F443362E"
                          : "#ECEDF4",
                      width: 113,
                      height: 33,
                      fontSize: "12px",
                      fontWeight: 600,
                      color:
                        item.invoice_status === 2 || item?.invoice_status === 3
                          ? "#F44336"
                          : item.invoice_status === 1
                          ? "#3DC826"
                          : "#01060B",
                    }}
                  >
                    {item.invoice_status === 0 && "Draft"}
                    {item.invoice_status === 1 && "Paid"}
                    {item.invoice_status === 2 && "Due"}
                  </Button>
                </TableCell>
                <TableCell
                  style={{
                    position: isMobile || isTablet ? "relative" : "static",
                  }}
                >
                  {/**NEW options */}
                  <Box>
                    <IconButton
                      onClick={(e) => handleOpen(e, item.id)}
                      disableRipple
                      sx={{
                        padding: 1,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                        "&:active": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      key={`menu-${item.id}-${item.invoice_status}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && openMenuInvoiceId === item.id}
                      onClose={() => handleClose()}
                      PaperProps={{
                        sx: {
                          border: "0.5px solid #A7C7EA",
                          backgroundColor: "white",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                          borderRadius: "8px",
                          width: "165px",
                        },
                      }}
                      MenuListProps={{
                        disablePadding: true,
                      }}
                    >
                      {(() => {
                        let filteredOptions = OPTIONS;

                        // Convert invoice_status to number for consistent comparison
                        const invoiceStatus = Number(item.invoice_status);

                        // Filter options based on invoice status
                        if (invoiceStatus === 0) {
                          // Draft invoices: View, Edit, Delete
                          filteredOptions = OPTIONS.filter(
                            (opt) =>
                              opt.name === "Edit" || opt.name === "Delete"
                          );
                        } else if (invoiceStatus === 1) {
                          // Paid invoices: View only
                          filteredOptions = OPTIONS.filter(
                            (opt) => opt.name === "View"
                          );
                        } else if (invoiceStatus === 2) {
                          // Due invoices: View, Send, Copy, Delete
                          filteredOptions = OPTIONS.filter(
                            (opt) =>
                              opt.name === "View" ||
                              opt.name === "Send" ||
                              opt.name === "Delete"
                          );
                        }

                        return filteredOptions.flatMap((option, idx) => {
                          // For Due invoices, show Send and Copy as separate options
                          if (
                            item.invoice_status === 2 &&
                            option.name === "Send" &&
                            item?.invoice_pdf
                          ) {
                            return [
                              <MenuItem
                                key="Send"
                                onClick={() => handleClose(option.name)}
                                disableRipple
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                  "&:active": {
                                    backgroundColor: "transparent",
                                  },
                                  borderBottom:
                                    idx !== filteredOptions.length - 1
                                      ? "1px solid #D2D7DC"
                                      : "",
                                  padding: "7px 16px",
                                  fontWeight: 500,
                                  color: "#6C6F71",
                                  fontSize: "16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  cursor: "pointer",
                                }}
                              >
                                {option.icon}
                                {option.name}
                              </MenuItem>,
                              <MenuItem
                                key="Copy"
                                onClick={() => {
                                  // This link is shared with the customer to
                                  // pay, so it must be the Stripe hosted url.
                                  const link = getInvoiceViewUrl(item);
                                  if (!link) {
                                    setAnchorEl(null);
                                    setOpenMenuInvoiceId(null);
                                    dispatch<any>(
                                      setToast({
                                        message:
                                          "This invoice has no link available yet.",
                                        type: "error",
                                        title: "Error",
                                        onClose: () => {},
                                      })
                                    );
                                    return;
                                  }
                                  navigator.clipboard.writeText(link);
                                  setAnchorEl(null);
                                  setOpenMenuInvoiceId(null);
                                  dispatch<any>(
                                    setToast({
                                      message: "Link copied to clipboard!",
                                      type: "success",
                                      title: "Copied",
                                      onClose: () => {},
                                    })
                                  );
                                }}
                                disableRipple
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                  "&:active": {
                                    backgroundColor: "transparent",
                                  },
                                  borderBottom:
                                    idx !== filteredOptions.length - 1
                                      ? "1px solid #D2D7DC"
                                      : "",
                                  padding: "7px 16px",
                                  fontWeight: 500,
                                  color: "#6C6F71",
                                  fontSize: "16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  cursor: "pointer",
                                }}
                              >
                                <ContentCopyIcon
                                  sx={{
                                    mr: -0.5,
                                    color: "#2977CC",
                                    width: "18px",
                                  }}
                                />
                                Copy invoice
                              </MenuItem>,
                            ];
                          }
                          // Default rendering for other options
                          return (
                            <MenuItem
                              key={option.name}
                              onClick={() => handleClose(option.name)}
                              disableRipple
                              sx={{
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                                "&:active": {
                                  backgroundColor: "transparent",
                                },
                                borderBottom:
                                  idx !== filteredOptions.length - 1
                                    ? "1px solid #D2D7DC"
                                    : "",
                                padding: "7px 16px",
                                fontWeight: 500,
                                color: "#6C6F71",
                                fontSize: "16px",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                cursor: "pointer",
                              }}
                            >
                              {option.icon}
                              {option.name}
                            </MenuItem>
                          );
                        });
                      })()}
                    </Menu>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </CustomTable>
        {((data?.invoices?.length === 0 && !filterLoading && !searchLoading) ||
          !data?.invoices) && (
          <div
            style={{
              textAlign: "center",
              marginTop: 20,
            }}
          >
            No invoice found!
          </div>
        )}
        {/* Pagination */}
        {Math.ceil(total_records / limit) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
            <CommonPagination
              currentPage={pageNo}
              totalPages={Math.ceil(total_records / limit)}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </div>
      <Modal open={deleteModalOpen} onClose={handleDeleteCancel}>
        <ModalWrapper
          sx={{
            width: isMobile ? "350px" : "500px",
            p: 0,
            height: "370px",
            backgroundImage: "none",
            position: "relative",
            borderRadius: "20px",
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* Top right decoration */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <ModalTop />
          </div>
          {/* Bottom left decoration */}
          <div
            style={{
              position: "absolute",
              bottom: -6,
              left: 0,
              zIndex: 1,
            }}
          >
            <ModalBottom />
          </div>
          {/* Ensure close button is above decorations */}
          <CloseButtonWrapper
            sx={{ zIndex: 10, position: "relative", marginTop: 1 }}
          >
            <CloseButton onClick={handleDeleteCancel}>
              <CrossIcon />
            </CloseButton>
          </CloseButtonWrapper>
          <Box sx={{ p: 4, pt: 0 }}>
            {/* Trash icon with red bg */}
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}
            >
              <Box
                sx={{
                  background: "#F20F0014",
                  borderRadius: "50%",
                  p: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrashIconForPopup />
              </Box>
            </Box>
            <div
              style={{
                textAlign: "center",
                fontSize: isMobile ? "20px" : "24px",
                color: "#01060B",
                fontWeight: 700,
                marginTop: 0,
                marginBottom: 10,
              }}
            >
              Delete Invoice
            </div>
            <div
              style={{
                width: isMobile ? "100%" : "250px",
                textAlign: "center",
                lineHeight: "24px",
                margin: "0 auto",
                fontSize: isMobile ? "12px" : "16px",
                color: "#6C6F71",
                fontWeight: 500,
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              Are you sure you want to delete this invoice?
            </div>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 3,
                justifyContent: "center",
              }}
            >
              <Button
                sx={{
                  border: "2px solid #2977CC",
                  borderRadius: "8px",
                  width: "192px",
                  height: "51px",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#2977CC",
                }}
                onClick={handleDeleteCancel}
              >
                Close
              </Button>
              <Button
                sx={{
                  background: "linear-gradient(to right, #2977CC, #1D538F)",
                  color: "white",
                  width: "192px",
                  height: "51px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
                onClick={handleDeleteConfirm}
              >
                Yes
              </Button>
            </Box>
          </Box>
        </ModalWrapper>
      </Modal>
    </div>
  );
};

export default StripeInvoiceTable;
